import React from 'react';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../apiService';

/**
 * BAR-1294: the in-app landing spot for "manage billing".
 *
 * Two callers, and the second is why this is a page rather than a button.
 * BAR-1295's paused email sends the customer here by plain link, and a link
 * cannot carry a bearer token -- so the token exchange has to happen in the
 * app, on arrival. Hence the automatic attempt on mount: for the paused
 * customer the whole recovery is one click from their inbox.
 *
 * EXCEPT WHEN THEY ARE COMING BACK. Stripe's `return_url` points here too, and
 * an unconditional mount attempt turned that into a redirect loop: pay, click
 * "Return to BARK", land here, get sent straight back to Stripe, and escape
 * only by burning the rate limit into a 429. `?returned=1` is how the two
 * arrivals are told apart -- the return lands on a confirmation instead.
 *
 * Deliberately NOT module-gated in the router. A paused customer's module map
 * is all false, and this is the page that lets them fix that.
 */
export default function BillingRoute() {
  const { authToken } = useAuth();
  const [searchParams] = useSearchParams();
  const returned = searchParams.get('returned') === '1';
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const attempted = React.useRef(false);

  const open = React.useCallback(async () => {
    if (!authToken) {
      setError('Please sign in to manage billing.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { url } = await apiService.createBillingPortalSession(authToken);
      // Full navigation, not a new tab: Stripe's own "return" brings the
      // customer back here with `?returned=1`.
      window.location.assign(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not open billing.');
      setBusy(false);
    }
  }, [authToken]);

  React.useEffect(() => {
    if (returned || attempted.current || !authToken) return;
    attempted.current = true;
    void open();
  }, [returned, authToken, open]);

  return (
    <div className="h-full bg-bg flex flex-col">
      <div className="border-b border-border-default px-6 py-4">
        <h1 className="text-lg font-semibold text-fg">Billing</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-xl space-y-4">
          {returned && !error && (
            <p className="text-sm text-fg-secondary">
              You're back from the billing portal. Any change you made is
              already saved. If you added a payment method, your access is
              restored.
            </p>
          )}

          {busy && !error && (
            <div className="flex items-center gap-2 text-sm text-fg-tertiary">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span>Opening your billing portal…</span>
            </div>
          )}

          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}

          {!busy && (
            <button
              type="button"
              onClick={() => void open()}
              className="inline-flex items-center gap-2 min-h-8 px-4 py-2 bg-primary text-on-primary text-xs font-semibold rounded-md shadow-bark-xs hover:bg-primary-hover hover:shadow-bark-md focus-visible:outline-none focus-visible:shadow-bark-focus disabled:opacity-60 transition-all duration-150"
            >
              {error ? 'Try again' : 'Open billing portal'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
